import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, CalendarDays, PartyPopper, RotateCcw, CalendarPlus, Trophy, Timer, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { downloadPartyIcs } from '../lib/calendar';
import { fmtTime } from './LeaderboardDialog';
import { shareResult } from '../lib/shareCard';

/* Compact arrival invite that fits within the cockpit (no scroll). */
export const ArrivalInvite = ({ onRsvp, onReplay, onLeaderboard, driveTimeMs }) => {
  const addToCalendar = () => {
    try {
      downloadPartyIcs();
      toast.success('Added to calendar!', {
        description: '24 Oct, 5:00-8:00 PM - check your downloads to open it.',
      });
    } catch (e) {
      toast.error('Could not create the calendar file. Please try again.');
    }
  };

  const onShare = async () => {
    const res = await shareResult(driveTimeMs);
    if (res.method === 'cancel') return;
    if (res.method === 'fallback') {
      toast.success('Result card saved & caption copied!', { description: 'Share it with your friends!' });
    } else {
      toast.success('Sharing your result!');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 170, damping: 18 }}
      className="h-full flex flex-col"
      data-testid="arrival-invite"
    >
      {/* hero */}
      <div
        className="relative rounded-2xl overflow-hidden border border-white/10 px-4 pt-5 pb-4 text-center"
        style={{
          background:
            'radial-gradient(120% 120% at 50% 0%, rgba(255,210,63,0.22), transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
        }}
      >
        <div className="flex flex-wrap items-center justify-center gap-2">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--hazard-yellow))] px-3 py-1 text-[#0e1217] font-display tracking-[0.2em] text-xs">
            <PartyPopper size={14} /> YOU'VE ARRIVED!
          </div>
          {driveTimeMs != null && (
            <div
              data-testid="arrival-time"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0e1217]/70 border border-white/15 px-3 py-1 text-white font-display tracking-wider text-xs"
            >
              <Timer size={13} className="text-[hsl(var(--hazard-yellow))]" /> {fmtTime(driveTimeMs)}
            </div>
          )}
        </div>
        <h1 className="mt-3 font-display tracking-wide text-5xl text-white leading-none">ADVIT TURNS 8!</h1>
        <p className="mt-1.5 text-white/60 text-sm font-medium">A construction-crew birthday bash — bring your hard hat!</p>
      </div>

      {/* details */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        <DetailTile icon={<CalendarDays size={16} />} kicker="DATE" value="24 Oct" />
        <DetailTile icon={<Clock size={16} />} kicker="TIME" value="5–8 PM" sub="17:00–20:00 IST" />
        <DetailTile icon={<MapPin size={16} />} kicker="PLACE" value="Cottage 837" />
      </div>

      {/* revealed party signs */}
      <div className="mt-3 space-y-2">
        <SignRow icon={<MapPin size={15} />} label="All roads lead to Cottage 837" />
        <SignRow icon={<Clock size={15} />} label="17:00 Hrs IST till 20:00 Hrs" />
      </div>

      {/* actions pinned to bottom */}
      <div className="mt-auto pt-3 space-y-2">
        <div className="flex gap-2">
          <Button
            onClick={addToCalendar}
            data-testid="add-to-calendar-button"
            className="flex-1 chunky-btn bg-[hsl(var(--hazard-yellow))] hover:bg-[hsl(43_100%_55%)] text-[#0e1217] font-semibold py-5 text-base"
          >
            <CalendarPlus size={18} /> Add to Calendar
          </Button>
          <Button
            onClick={onLeaderboard}
            aria-label="Leaderboard"
            data-testid="leaderboard-button"
            className="chunky-btn bg-[hsl(var(--hazard-yellow))] hover:bg-[hsl(43_100%_55%)] text-[#0e1217] font-semibold py-5 px-4"
          >
            <Trophy size={18} className="text-[#0e1217]" />
          </Button>
          <Button
            onClick={onShare}
            aria-label="Share result"
            data-testid="share-result-button"
            className="chunky-btn bg-[hsl(var(--hazard-yellow))] hover:bg-[hsl(43_100%_55%)] text-[#0e1217] font-semibold py-5 px-4"
          >
            <Share2 size={18} className="text-[#0e1217]" />
          </Button>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={onRsvp}
            data-testid="arrival-rsvp-button"
            className="flex-1 chunky-btn bg-[hsl(var(--orange))] hover:bg-[hsl(18_100%_50%)] text-white font-semibold py-5 text-base"
          >
            <PartyPopper size={18} /> RSVP Now
          </Button>
          <Button
            onClick={onReplay}
            aria-label="Drive again"
            data-testid="replay-button"
            className="chunky-btn glass text-white hover:bg-white/10 font-semibold py-5 px-4"
          >
            <RotateCcw size={16} />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

const DetailTile = ({ icon, kicker, value, sub }) => (
  <div className="glass-2 rounded-xl p-2.5 text-center" data-testid="invite-details-title">
    <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--hazard-yellow))] text-[#0e1217]">
      {icon}
    </div>
    <div className="mt-1.5 font-display tracking-[0.15em] text-[10px] text-[hsl(var(--hazard-yellow))]">{kicker}</div>
    <div className="font-semibold text-sm text-white leading-tight">{value}</div>
    {sub && <div className="text-[9px] text-white/50 font-mono-tel mt-0.5">{sub}</div>}
  </div>
);

const SignRow = ({ icon, label }) => (
  <div className="flex items-center gap-2.5 rounded-xl glass-2 px-3 py-2">
    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--orange))] text-white">
      {icon}
    </span>
    <span className="text-sm font-semibold text-white leading-tight">{label}</span>
  </div>
);
